import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Catchment Set
      </Button>
    </Col>
  </Row>
);

const TableHeader = () => (
  <div>
    <Row>
      <Col sm={4} className="table-header">
        Catchment Set name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Insight Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const shapeSet = props.item;
  return (
    <Row key={shapeSet.id}>
      <Col sm={4} className="table-entry">
        {shapeSet.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(shapeSet.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(shapeSet.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        {!shapeSet.shareWithAllIndustries && (
          <Button
            className="relative-padding"
            bsStyle="link"
            onClick={() => props.removeItem(shapeSet)}
            aria-label={`Remove ${shapeSet.name}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class IndustryShapeSets extends React.Component {
  static displayName = 'IndustryShapeSets';

  constructor(props) {
    super(props);
    this.state = {
      newShapeSetId: undefined,
      showNewShapeSet: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    // get allocated and unallocated shape sets
    const currentIndustryId = parseInt(this.props.params.industryId, 10);
    this.props.getCurrentIndustryShapeSets(currentIndustryId, true);
    this.props.getCurrentIndustryShapeSets(currentIndustryId, false);

    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  addShapeSet = () => {
    if (this.state.newShapeSetId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const industryId = parseInt(this.props.params.industryId, 10);
      const newShapeSetId = this.state.newShapeSetId;
      this.props
        .addShapeSetToIndustry(newShapeSetId, industryId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewShapeSet: false, newShapeSetId: undefined });
  };

  removeShapeSet = shapeSet => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const industryId = parseInt(this.props.params.industryId, 10);
    this.props
      .removeShapeSetFromIndustry(shapeSet, industryId)
      .then(response => this.success(response), error => this.failure(error));
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  selectOnChange = newShapeSetId => {
    this.setState({ newShapeSetId });
  };

  addButtonClick = filterValue => {
    this.setState({ showNewShapeSet: !this.state.showNewShapeSet });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  cancel = () => {
    this.setState({ showNewShapeSet: false, newShapeSetId: undefined });
  };

  render() {
    const { unallocated, allocated } = this.props.currentIndustry.shapeSets;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeShapeSet} />
    );

    return (
      <div>
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing catchment sets for industry {this.props.currentIndustry.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewShapeSet && (
          <AddNewRow
            items={unallocated}
            addItem={this.addShapeSet}
            selectValue={this.state.newShapeSetId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="catchment sets"
          tableHeader={TableHeader}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default IndustryShapeSets;



// WEBPACK FOOTER //
// ./src/containers/industries/IndustryShapeSets.js